import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import AnimeCard from '@/components/feature/AnimeCard';
import { animeCategories, mangaCategories } from '@/mocks/categories';
import { allWorks, filterWorksByCategory, type Work } from '@/mocks/works';

const PAGE_SIZE = 12;

function keywordMatch(w: Work, q: string): boolean {
  const text = `${w.title} ${w.titleEn} ${w.tags.join(' ')} ${w.description}`.toLowerCase();
  return text.includes(q.toLowerCase());
}

export default function CategoryPage() {
  const { id = 'all' } = useParams();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get('q') || '').trim();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const tabs = [
    { id: 'all', label: t('cat_all') },
    ...animeCategories.map((c) => ({ id: c.id, label: c.name })),
    ...mangaCategories.map((c) => ({ id: c.id, label: c.name })),
  ];

  const activeTab = tabs.some((tb) => tb.id === id) ? id : 'all';

  const baseWorks = useMemo(() => {
    const byCat = filterWorksByCategory(activeTab);
    if (q) return byCat.filter((w) => keywordMatch(w, q));
    return byCat;
  }, [activeTab, q]);

  const totalPages = Math.max(1, Math.ceil(baseWorks.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageWorks = baseWorks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const changeTab = (tabId: string) => {
    setPage(1);
    navigate(`/category/${tabId}`);
  };

  const activeCat = tabs.find((tb) => tb.id === activeTab);

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: t('cat_breadcrumb_home'), item: 'https://mangaplexx.github.io/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: activeCat?.label || t('cat_all'),
                item: `https://mangaplexx.github.io/category/${activeTab}`,
              },
            ],
          }),
        }}
      />
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <Breadcrumb items={[{ label: activeCat?.label || t('cat_all') }]} />

        <header className="mt-6">
          <h1 className="font-heading text-3xl text-foreground-950">
            {q ? `搜索：${q}` : activeCat?.label || t('cat_title')}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">{t('cat_subtitle')}</p>
        </header>

        {/* 分类切换标签 */}
        <div
          className="mt-6 flex flex-wrap gap-2"
          role="tablist"
          aria-label={t('cat_title')}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => changeTab(tab.id)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-secondary-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-foreground-600">
            {t('cat_total', { count: baseWorks.length })}
          </p>
          <span className="flex items-center gap-1 text-sm text-foreground-500">
            <i className="ri-filter-3-line" aria-hidden="true"></i>
            {t('tag_ranking')}: <span className="text-foreground-950">{t('tag_rating')}</span>
          </span>
        </div>

        {/* 作品网格 */}
        {pageWorks.length > 0 ? (
          <div
            className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
            data-product-shop
          >
            {pageWorks.map((work) => (
              <AnimeCard key={work.id} work={work} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/50 py-16 text-center">
            <i className="ri-inbox-line text-4xl text-foreground-400" aria-hidden="true"></i>
            <p className="mt-3 text-sm text-foreground-600">{t('cat_empty')}</p>
          </div>
        )}

        {/* 分页 */}
        {totalPages > 1 && (
          <nav
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
            aria-label="分页导航"
          >
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
              {t('cat_pagination_prev')}
            </button>

            <span className="px-2 text-sm text-foreground-600">
              {t('cat_page_info', { page: currentPage, total: totalPages })}
            </span>

            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('cat_pagination_next')}
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            </button>
          </nav>
        )}
      </div>
    </Layout>
  );
}